import React, { useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import api, { extractError } from "@/api/client";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogTrigger, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Building2, Plus, MapPin, Upload, Briefcase, Image as ImageIcon } from "lucide-react";
import { toast } from "sonner";
import AuthImage from "@/components/AuthImage";
import ContextualHelpLink from "@/components/ContextualHelpLink";
import { ASSET_TYPES } from "@/lib/dict";
import { usePickLists } from "@/hooks/usePickLists";

const emptyForm = {
  name: "", type: "office", address: "", city: "", country: "France",
  client_id: "", legal_entity_id: "",
  internal_ref: "", postal_code: "",
  latitude: "", longitude: "", cadastral_ref: "",
  total_surface: "", number_of_buildings: 1,
  delivery_date: "", notes: "",
  photo_url: "",
};

export default function Assets() {
  const qc = useQueryClient();
  const pl = usePickLists();
  const { data: assets = [] } = useQuery({ queryKey: ["assets"], queryFn: () => api.get("/assets").then((r) => r.data) });
  const { data: clients = [] } = useQuery({ queryKey: ["clients"], queryFn: () => api.get("/clients").then((r) => r.data) });
  const { data: entities = [] } = useQuery({ queryKey: ["legal-entities"], queryFn: () => api.get("/legal-entities").then((r) => r.data) });
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [photoUploading, setPhotoUploading] = useState(false);
  const [filterClient, setFilterClient] = useState("all");

  const filteredEntities = useMemo(
    () => form.client_id ? entities.filter((e) => e.client_id === form.client_id) : [],
    [entities, form.client_id]
  );

  const visibleAssets = useMemo(
    () => filterClient === "all" ? assets : assets.filter((a) => a.client_id === filterClient),
    [assets, filterClient]
  );

  const clientMap = useMemo(() => Object.fromEntries(clients.map((c) => [c.id, c.name])), [clients]);
  const entityMap = useMemo(() => Object.fromEntries(entities.map((e) => [e.id, e.name])), [entities]);

  const create = useMutation({
    mutationFn: (d) => api.post("/assets", d),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["assets"] });
      toast.success("Actif créé");
      setOpen(false); setForm(emptyForm);
    },
    onError: (e) => toast.error(extractError(e, "Création impossible")),
  });

  const handlePhotoUpload = async (file) => {
    if (!file) return;
    setPhotoUploading(true);
    try {
      // Upload without asset_id yet — the file is orphaned admin-owned until asset creation.
      // For simplicity we require the caller (super_admin/org_admin) or we upload later after asset exists.
      // Here we let the user pick a file first, keep the File object in state and upload after create.
      // But since we can't upload without object linkage for non-admins, we defer.
      const reader = new FileReader();
      reader.onload = (e) => setForm((f) => ({ ...f, _pendingPhoto: file, _photoPreview: e.target?.result }));
      reader.readAsDataURL(file);
    } finally {
      setPhotoUploading(false);
    }
  };

  const submit = async () => {
    // Clean payload: remove empty strings/nulls, coerce numbers.
    const raw = { ...form };
    delete raw._pendingPhoto;
    delete raw._photoPreview;
    const payload = {};
    for (const [k, v] of Object.entries(raw)) {
      if (v === "" || v === null || v === undefined) continue;
      if (["total_surface", "latitude", "longitude"].includes(k)) payload[k] = Number(v);
      else if (k === "number_of_buildings") payload[k] = parseInt(v, 10) || 1;
      else payload[k] = v;
    }
    if (!payload.legal_entity_id) delete payload.legal_entity_id;
    if (!payload.client_id) delete payload.client_id;

    try {
      const { data: asset } = await api.post("/assets", payload);
      // If a photo file was staged, upload it now with the fresh asset id and patch photo_url.
      if (form._pendingPhoto) {
        const fd = new FormData();
        fd.append("file", form._pendingPhoto);
        try {
          const { data: doc } = await api.post(`/upload?asset_id=${asset.id}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
          await api.patch(`/assets/${asset.id}`, { photo_url: doc.url });
        } catch (e) {
          toast.warning("Actif créé, mais photo non enregistrée");
        }
      }
      qc.invalidateQueries({ queryKey: ["assets"] });
      toast.success("Actif créé");
      setOpen(false); setForm(emptyForm);
    } catch (e) {
      toast.error(extractError(e, "Création impossible"));
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Module</div>
          <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight text-slate-900 mt-1">Portefeuille / Actifs</h1>
          <p className="text-sm text-slate-500 mt-1.5">{visibleAssets.length} / {assets.length} actif(s) sous gestion</p>
          <div className="mt-1.5"><ContextualHelpLink page="assets" /></div>
        </div>
        <div className="flex items-center gap-3">
          {clients.length > 0 && (
            <Select value={filterClient} onValueChange={setFilterClient}>
              <SelectTrigger data-testid="filter-client" className="w-52 bg-white"><SelectValue placeholder="Filtrer par client" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous les clients</SelectItem>
                {clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="new-asset-btn" className="text-white" style={{ background: "#023E73" }}><Plus size={16} className="mr-1.5" /> Nouvel actif</Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto">
              <DialogHeader><DialogTitle>Créer un actif</DialogTitle></DialogHeader>
              <div className="space-y-4">
                {/* Photo */}
                <div>
                  <Label>Photo principale</Label>
                  <div className="mt-1 flex items-center gap-3">
                    <div className="w-24 h-24 rounded-md border-2 border-dashed border-slate-200 flex items-center justify-center bg-slate-50 overflow-hidden">
                      {form._photoPreview
                        ? <img src={form._photoPreview} alt="preview" className="w-full h-full object-cover" />
                        : <ImageIcon size={20} className="text-slate-300" />}
                    </div>
                    <label className="cursor-pointer">
                      <input data-testid="asset-photo-input" type="file" accept="image/*" className="hidden" onChange={(e) => handlePhotoUpload(e.target.files?.[0])} />
                      <div className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#023E73] border border-slate-200 rounded-md px-3 py-2 hover:bg-slate-50">
                        <Upload size={12} /> {photoUploading ? "..." : (form._pendingPhoto ? "Changer la photo" : "Ajouter une photo")}
                      </div>
                    </label>
                  </div>
                </div>

                {/* Client hierarchy */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Client</Label>
                    <Select value={form.client_id} onValueChange={(v) => setForm({ ...form, client_id: v, legal_entity_id: "" })}>
                      <SelectTrigger data-testid="asset-client"><SelectValue placeholder="—" /></SelectTrigger>
                      <SelectContent>
                        {clients.length === 0 && <div className="px-3 py-2 text-xs text-slate-400">Aucun client — crée-en d'abord</div>}
                        {clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Entité juridique</Label>
                    <Select value={form.legal_entity_id} onValueChange={(v) => setForm({ ...form, legal_entity_id: v })} disabled={!form.client_id}>
                      <SelectTrigger data-testid="asset-legal-entity"><SelectValue placeholder={form.client_id ? "—" : "Sélectionne d'abord un client"} /></SelectTrigger>
                      <SelectContent>
                        {filteredEntities.length === 0 && form.client_id && <div className="px-3 py-2 text-xs text-slate-400">Aucune entité pour ce client</div>}
                        {filteredEntities.map((e) => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Identité */}
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Nom de l'actif *</Label><Input data-testid="asset-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
                  <div><Label>Référence interne</Label><Input data-testid="asset-internal-ref" placeholder="REF-2026-001" value={form.internal_ref} onChange={(e) => setForm({ ...form, internal_ref: e.target.value })} /></div>
                </div>

                <div>
                  <Label>Type</Label>
                  <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                    <SelectTrigger data-testid="asset-type"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {pl.options("asset.type", ASSET_TYPES).map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>

                {/* Adresse */}
                <div><Label>Adresse</Label><Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} /></div>
                <div className="grid grid-cols-3 gap-3">
                  <div><Label>Code postal</Label><Input data-testid="asset-postal-code" value={form.postal_code} onChange={(e) => setForm({ ...form, postal_code: e.target.value })} /></div>
                  <div><Label>Ville</Label><Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} /></div>
                  <div><Label>Pays</Label><Input value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} /></div>
                </div>

                {/* Géo & cadastre */}
                <div className="grid grid-cols-3 gap-3">
                  <div><Label>Latitude</Label><Input data-testid="asset-lat" type="number" step="0.000001" placeholder="48.8566" value={form.latitude} onChange={(e) => setForm({ ...form, latitude: e.target.value })} /></div>
                  <div><Label>Longitude</Label><Input data-testid="asset-lng" type="number" step="0.000001" placeholder="2.3522" value={form.longitude} onChange={(e) => setForm({ ...form, longitude: e.target.value })} /></div>
                  <div><Label>Réf. cadastrale</Label><Input data-testid="asset-cadastral" placeholder="AB123" value={form.cadastral_ref} onChange={(e) => setForm({ ...form, cadastral_ref: e.target.value })} /></div>
                </div>

                {/* Caractéristiques */}
                <div className="grid grid-cols-3 gap-3">
                  <div><Label>Surface (m²)</Label><Input type="number" step="0.01" value={form.total_surface} onChange={(e) => setForm({ ...form, total_surface: e.target.value })} /></div>
                  <div><Label>Bâtiments</Label><Input type="number" min="1" value={form.number_of_buildings} onChange={(e) => setForm({ ...form, number_of_buildings: e.target.value })} /></div>
                  <div><Label>Date livraison</Label><Input type="date" value={form.delivery_date} onChange={(e) => setForm({ ...form, delivery_date: e.target.value })} /></div>
                </div>

                <div><Label>Notes</Label><Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
              </div>
              <DialogFooter>
                <Button data-testid="asset-submit" onClick={submit} disabled={!form.name || !form.address || !form.city || create.isPending} className="text-white" style={{ background: "#023E73" }}>
                  {create.isPending ? "Création…" : "Créer l'actif"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {visibleAssets.length === 0 ? (
        <div data-testid="assets-empty-state" className="text-center py-16 border border-dashed border-slate-200 rounded-xl bg-white">
          <Building2 size={32} className="mx-auto mb-3 text-slate-300" />
          <p className="text-sm font-semibold text-slate-600">Aucun actif ne vous est attribué</p>
          <p className="text-xs text-slate-400 mt-1">Contactez votre administrateur pour obtenir l&apos;accès à un ou plusieurs sites.</p>
        </div>
      ) : (
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {visibleAssets.map((a) => (
          <Link to={`/assets/${a.id}`} key={a.id} data-testid={`asset-card-${a.id}`}>
            <Card className="p-0 hover:shadow-md hover:-translate-y-0.5 transition-all border-slate-200 h-full overflow-hidden">
              <div className="relative h-32 bg-slate-100">
                {a.photo_url
                  ? <AuthImage src={a.photo_url} alt={a.name} className="w-full h-full object-cover" fallback={<div className="w-full h-full flex items-center justify-center"><Building2 size={28} className="text-slate-300" /></div>} />
                  : <div className="w-full h-full flex items-center justify-center"><Building2 size={28} className="text-slate-300" /></div>}
                <div className={`absolute top-2 right-2 px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase ${a.risk_score > 70 ? "bg-red-500 text-white" : a.risk_score > 50 ? "bg-amber-500 text-white" : "bg-emerald-500 text-white"}`}>Risk {a.risk_score}</div>
              </div>
              <div className="p-5">
                <div className="flex items-start justify-between gap-2">
                  <h3 className="font-display font-bold text-lg text-slate-900 truncate">{a.name}</h3>
                  {a.internal_ref && <span className="text-[10px] font-mono text-slate-500 flex-shrink-0 mt-1">{a.internal_ref}</span>}
                </div>
                {a.type && <span data-testid={`asset-type-${a.id}`} className="inline-block mt-1 text-[10px] font-semibold uppercase tracking-wider text-[#023E73] bg-[#023E73]/10 rounded-full px-2 py-0.5">{pl.labelOf("asset.type", a.type, ASSET_TYPES)}</span>}
                {a.client_id && (
                  <div className="flex items-center gap-1 text-[11px] text-[#023E73] mt-1 font-semibold">
                    <Briefcase size={11} /> {clientMap[a.client_id] || "Client"}
                    {a.legal_entity_id && entityMap[a.legal_entity_id] && <span className="text-slate-500 font-normal"> — {entityMap[a.legal_entity_id]}</span>}
                  </div>
                )}
                <div className="flex items-center gap-1 text-xs text-slate-500 mt-2"><MapPin size={12} /> {a.postal_code && `${a.postal_code} `}{a.city}, {a.country}</div>
                <div className="text-xs text-slate-500 mt-1">{a.address}</div>
                <div className="border-t border-slate-100 mt-4 pt-3 grid grid-cols-2 gap-3 text-xs">
                  <div><div className="text-slate-400 uppercase tracking-wider text-[10px] font-semibold">Surface</div><div className="font-semibold text-slate-900">{a.total_surface ? `${a.total_surface} m²` : "—"}</div></div>
                  <div><div className="text-slate-400 uppercase tracking-wider text-[10px] font-semibold">Bâtiments</div><div className="font-semibold text-slate-900">{a.number_of_buildings}</div></div>
                </div>
              </div>
            </Card>
          </Link>
        ))}
      </div>
      )}
    </div>
  );
}
